import { appModalActivatorAction, isCompactWidget } from '../../actions/App';
import {
  elementTogglerAction,
  nextStepAction,
  prevStepAction,
} from '../../actions/Section';
import {
  userDataChangeAction,
  userDataFormSubmitAction,
} from '../../actions/UserData';
import { validatorMap } from '../../constants/Validators';
import {
  getSectionById,
  getPageById,
  getStepsById,
} from '../section/sectionUtils';
import {
  resolvePath,
  anyOfTheKeysExists,
  replaceData,
  createFlowEvent,
} from '../common';
import { callService } from '../api/service';
import { callServices } from '../api/common';
import { validateCpr, isValidDate, validateMaxDate, validateMinDate, validateEmail } from './formValidators';
import { servicesLoadedAction, servicesLoadingAction } from '../../actions/Api';
import { sendTrygTealium } from '../tracking';

import * as icons from '../../assets/icons';

export const getCurrentFormChildren = (vNode) => {
  // Find the current step and the finished steps so far. Only these steps should be validated.
  const {
    props: {
      packageData,
      currentProgress: {
        currentSectionId,
        currentPageData: {
          currentPageId,
          currentStepData: {
            finishedSteps,
            currentStepId,
          },
        },
      },
      modalState,
      modalData,
    },
  } = vNode;

  const currentSection = getSectionById(currentSectionId, packageData);
  const currentPage = getPageById(currentPageId, currentSection);
  const stepsForValidation = [...finishedSteps, currentStepId];
  const childrenForValidation = [];

  // Loop steps and add Input fields to the childrenForValidation array.
  stepsForValidation.forEach(stepId => {
    const pageData = vNode.props.pageData
      ? vNode.props.pageData
      : currentPage;
    const steps = getStepsById(stepId, pageData);
    const stepsNotToValidate = ['Price'];
    steps.forEach(step => {
      if (step) {
        if (stepsNotToValidate.includes(step.component.componentName)) {
          return;
        }

        // Add the action Props for validation
        if (step.component.actionProps && step.component.actionProps.length > 0) {
          childrenForValidation.push(...step.component.actionProps);
        }

        childrenForValidation.push(...step.component.props);
      }
    });
  });

  if (modalState && modalState.active) {
    const modal = modalData[modalState.name];
    if (modal && modal.props) {
      childrenForValidation.push(...modal.props);
    }
  }

  return childrenForValidation;
};

// Validation
export const validation = (values, children) => {
  // Loop each child and check if a value has been added. Validate if needed.
  const errors = {};
  const childrenForValidation = getCurrentFormChildren(children[0]);

  childrenForValidation.forEach(child => {
    const { id } = child;

    // Return if the value has not been set.
    // Field can save to other form values too. Continue if this is the case.
    if (!child.formValues && !Object.hasOwnProperty.call(values, id)) {
      return;
    }

    // Check if the child has any validators set. Skip if none are set.
    if (
      !Object.hasOwnProperty.call(child, 'validators') ||
      child.validators.length < 1
    ) {
      return;
    }

    // Loop validators and run each one.
    child.validators.forEach(validator => {
      // Ignore if validator does not exist.
      if (!Object.hasOwnProperty.call(validatorMap, validator.name)) {
        return;
      }

      if (!validatorMap[validator.name](values, id, validator, child)) {
        // TODO: Create list which maps for default values
        errors[id] = validator.errorMsg;
      }
    });
  });

  return errors;
};

export const isInitialValid = (values, children) => {
  const childrenForValidation = getCurrentFormChildren(children[0]);
  let isValid = true;
  childrenForValidation.forEach(child => {
    const { id } = child;
    if (child.required) {
      isValid = isValid && Object.hasOwnProperty.call(values, id);
    }
  });

  return isValid;
};

/**
 * Calculates the amount of columns for each of the inputs
 * @param   {object} componentProp        The current components props
 * @returns {string}                      Return the amount of columns
 */
export const calcColumns = (componentProp) => {
  // Set the amount of columns
  const buttonComponents = ['radioButtons', 'checkboxButtons'];
  const boxComponents = ['radio', 'checkbox'];
  // If there's been inputted columns in the data, use that.
  if (componentProp.columns) {
    return componentProp.columns.toString();
  }
  // If the component is a button, default to 2 columns
  if (buttonComponents.includes(componentProp.type)) {
    return '2';
  }
  // If the component is a radio or checkbox, default to 1 columns
  if (boxComponents.includes(componentProp.type)) {
    return '1';
  }
  // If there's no columns, default 1 columns
  return '12';
};

/**
 * Returns the classes for loading input fields
 * @param   {object} loadingOnField       The loadingOnField prop from json.
 * @param   {object} userData             The userdata
 * @returns {string}                      Return the classes that should be appended
 */
export const isLoadingOnField = (loadingOnField, userData) => {
  if (anyOfTheKeysExists(userData, [loadingOnField]) &&
    userData[loadingOnField] === true) {
    return 'isLoading';
  }

  if (anyOfTheKeysExists(userData, [loadingOnField])) {
    return 'loaded';
  }

  return '';
};

export const changeFocusOnMaxLength = (
  event, endFocus, maxLength, onChange
) => {
  onChange(event);
  // If no endFocus, don't execute
  if (!endFocus) {
    return;
  }

  const eventValue = event.target.value.length.toString();
  const maxLengthValue = maxLength.toString();

  if (endFocus && !maxLengthValue) {
    console.warn('❌ You\'ll need a maxLength for the endFocus to work'); // eslint-disable-line
  }

  let endFocusElement = document.querySelector(`#${endFocus}`);
  const allNodes = document.getElementsByTagName(import.meta.env.VITE_WRAPPER_TAG);
  if (allNodes[0].shadowRoot) {
    endFocusElement = allNodes[0].shadowRoot.querySelector(`#${endFocus}`);
  }
  if (eventValue === maxLengthValue) {
    endFocusElement.focus();
  }
};

/**
 * Return validateTypeResult.
 * @param {object}  subResult
 * @param {boolean} isValid
 * @param {object}  item
 * @return {{isValid: *, message: *}}
 */
const validateTypeResult = (subResult, isValid, item) => {
  const isValidNew = subResult ? subResult.isValid && isValid : isValid;
  let newMessage = isValidNew ? '' : true;
  if (newMessage) {
    newMessage = Object.hasOwnProperty.call(item, 'message')
      ? item.message
      : '';
  }
  return {
    isValid: isValidNew,
    message: newMessage,
  };
};

/**
 * Return if the validate type is valid.
 * @param {object}  requiredData
 * @param {object}  formValues
 * @param {object}  userData
 * @param {boolean} isOr
 * @return {{isValid: *, message: *}}
 */
const validateType = (requiredData, formValues, userData, isOr) => {
  const results = requiredData.map(itemOriginal => {
    // Create empty object so we don't overwrite requiredData.
    const item = {};

    // Allow for dynamic item properties.
    Object.keys(itemOriginal).forEach(element => {
      item[element] = replaceData(itemOriginal[element], userData, false, false, true, true);
    });

    let subResult = null;
    let dataToTest;
    if (Object.hasOwnProperty.call(item, 'allof')) {
      subResult = validateType(item.allof, formValues, userData, false);
    }
    if (Object.hasOwnProperty.call(item, 'anyof')) {
      subResult = validateType(item.anyof, formValues, userData, true);
    }
    // Fix for IE with /\./ as regular expression is invalid (bug in IE with Compatibility View).
    const find = '[.]';
    const re = new RegExp(find, 'g');

    if (Object.hasOwnProperty.call(item, 'type') &&
      item.type === 'formValues') {
      dataToTest = formValues;
    } else {
      dataToTest = { ...formValues, ...userData };
    }

    // Field is not set. Check if it has the correct value.
    if (!Object.hasOwnProperty.call(dataToTest, item.field)) {
      // If the string includes dots, then try to match it as an object.
      if (re.test(item.field)) {
        dataToTest = resolvePath(dataToTest, item.field);
        if (!dataToTest) {
          if (Object.hasOwnProperty.call(item, 'notField') &&
            !Object.hasOwnProperty.call(dataToTest, item.notField)) {
            return validateTypeResult(subResult, true, item);
          }
          return validateTypeResult(subResult, false, item);
        }
      } else {
        if (Object.hasOwnProperty.call(item, 'notField') &&
          !Object.hasOwnProperty.call(dataToTest, item.notField)) {
          return validateTypeResult(subResult, true, item);
        }
        return validateTypeResult(subResult, false, item);
      }
    } else {
      dataToTest = dataToTest[item.field];
    }

    // Field is set. Check if it has the correct value.
    if (Object.hasOwnProperty.call(item, 'value') &&
      dataToTest !== item.value) {
      // If the string includes dots, then try to match it as an object.
      if (re.test(item.field) && resolvePath(dataToTest, item.field)) {
        return validateTypeResult(subResult, true, item);
      }
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'notEquals') &&
      dataToTest === item.boolValue) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'boolValue') &&
      dataToTest !== item.boolValue) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'stringValue') &&
      dataToTest !== item.stringValue) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'numericValue') &&
      dataToTest !== item.numericValue) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'nullValue') && dataToTest !== null) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'minLength') &&
      (dataToTest === null || dataToTest.length < item.minLength)) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'numbersOnly') &&
      !((/^\d+$/.test(dataToTest)))) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'emailFormat') &&
      !validateEmail(dataToTest)) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'minDate') &&
      !validateMinDate(dataToTest, formValues, item.minDate)) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'maxDate') &&
      !validateMaxDate(dataToTest, formValues, item.maxDate)) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'validDate') &&
      !isValidDate(dataToTest)) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'minNumber') &&
      (dataToTest === null || dataToTest < item.minNumber)) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'maxNumber') &&
      (dataToTest === null || dataToTest > item.maxNumber)) {
      return validateTypeResult(subResult, false, item);
    }
    if (Object.hasOwnProperty.call(item, 'endsWith') &&
      (dataToTest === null || dataToTest.slice(-1) != item.endsWith)) {
      return validateTypeResult(subResult, false, item);
    }
    // Check if there is any value.
    if (dataToTest === undefined || (dataToTest === '' && item.value !== '')) {
      return validateTypeResult(subResult, false, item);
    }
    // If we land here it means that the field is valid. Force rest iterations to return true.
    return validateTypeResult(subResult, true, item);
  });

  return results.reduce((combined, result) => {
    if (isOr) {
      if (combined && combined.isValid) {
        return combined;
      }
    } else if (combined && !combined.isValid) {
      return combined;
    }
    return result;
  });
};

/**
 * Return if validate all of object.
 * @param {object} requiredData
 * @param {object}  formValues
 * @param {object}  userData
 * @return {{isValid: *, message: *}}
 */
const validateAllOf = (requiredData, formValues, userData) => (
  validateType(requiredData, formValues, userData, false)
);

/**
 * Return if validate any of object.
 * @param {object} requiredData
 * @param {object}  formValues
 * @param {object}  userData
 * @return {{isValid: *, message: *}}
 */
const validateAnyOf = (requiredData, formValues, userData) => (
  validateType(requiredData, formValues, userData, true)
);

/**
 * Validates that the correct fields has been set for step to be valid.
 * @param  {array}  requiredData  Data that should be set.
 * @param  {object} userData      The userData store.
 * @param  {object} formValues    The current form values.
 * @return {object}               Object containing if the step is valid and a text if not.
 */
const validateStep = (requiredData, userData, formValues) => {
  let returnObj = {
    'isValid': true,
    'message': null,
  };

  let allOfObj = null;
  let anyOfObj = null;

  if (requiredData.allof) {
    allOfObj = validateAllOf(requiredData.allof, formValues, userData);
    returnObj = allOfObj;
  }

  if (requiredData.anyof) {
    anyOfObj = validateAnyOf(requiredData.anyof, formValues, userData);
    if (allOfObj) {
      if ((!allOfObj.isValid || !anyOfObj.isValid)) {
        returnObj = {
          isValid: false,
          message: allOfObj.message ? allOfObj.message : anyOfObj.message,
        };
      }
    } else {
      returnObj = anyOfObj;
    }
  }
  if(requiredData.notIncluded) {
    const requiredValue = requiredData.notIncluded[0];
    const values = replaceData(requiredValue.value, userData, false, false, false);
    let newData = '';
    if (userData[requiredValue.key] && userData[requiredValue.key] !== '') {
      newData = (userData[requiredValue.key]).toUpperCase();
    }
    returnObj = {
      isValid: !values.includes(newData),
      message: '',
    };
  }
  return returnObj;
};

/**
 * Validates that the correct fields has been set for step to be valid.
 * @param  {array}   requiredData Data that should be set.
 * @param  {object}  userData     The userData store.
 * @param  {object}  formValues   The current form values.
 * @return {boolean}              True if all data is set.
 */
export const stepHasRequiredData = (requiredData, userData, formValues) => {
  if (!requiredData) {
    return true;
  }
  const isStepValid = validateStep(requiredData, userData, formValues);
  return isStepValid.isValid;
};

const actionExecuteScrollToTop = (action) => {
  const elementToScroll = document.getElementsByClassName(action.props.scrollElement)[0];
  const isSmooth = action.props.smoothEnabled;
  const deviceWidth =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      document.body.clientWidth;

  if (deviceWidth > 768 && elementToScroll) {
    if (isSmooth) {
      elementToScroll.classList.add('smooth-scroll-animation');
      elementToScroll.classList.add('scroll-in-progress');

      const startScrollTop = window.scrollY;
      const targetScrollTop = elementToScroll.offsetTop;
      const duration = 1200; // Animation duration in milliseconds
      const startTime = performance.now();

      scrollAnimation(startTime, startScrollTop, targetScrollTop, duration, elementToScroll);
    } else {
      window.scrollTo({
        top: elementToScroll.offsetTop,
        behavior: 'smooth',
      });
    }
  }
};

function smoothScrollToElement(targetElement, duration) {
  // Calculate the target position relative to the top of the document
  const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
  // Get the current scroll position
  const startPosition = window.pageYOffset;
  // Calculate the distance to scroll
  const distance = targetPosition - startPosition;
  // Initialize the start time to null
  let startTime = null;

  // The animation function that will be called on each frame
  function animation(currentTime) {
      // Set the start time on the first call
      if (startTime === null) startTime = currentTime;
      // Calculate the time elapsed since the start of the animation
      const timeElapsed = currentTime - startTime;
      // Calculate the current scroll position using the easing function
      const run = ease(timeElapsed, startPosition, distance, duration);
      // Scroll to the calculated position
      window.scrollTo(0, run);
      // Continue the animation if the duration has not been reached
      if (timeElapsed < duration) requestAnimationFrame(animation);
  }

  // The easing function to make the scroll smooth
  function ease(time, start, change, duration) {
      // Normalize time to the range [0, 2]
      time /= duration / 2;

      // If the normalized time is less than 1, use the first half of the easing equation
      if (time < 1) {
          return (change / 2) * time * time + start;
      }

      // Adjust time for the second half of the easing equation
      time--;

      // Use the second half of the easing equation
      return (-change / 2) * (time * (time - 2) - 1) + start;
  }

  // Start the animation
  requestAnimationFrame(animation);
}

export const scrollToTop = (state) => {
  const wrapperTag = import.meta.env.VITE_WRAPPER_TAG;
  let findUserData = state;
  if(state.userData){
    findUserData = state.userData;
  }
  if(findUserData.scrollToTop !== true || isCompactWidget()){
    return;
  }

  const selector = document.querySelector(wrapperTag).querySelector('.widget-outer') ? document.querySelector(wrapperTag).querySelector('.widget-outer') : document.querySelector(wrapperTag).shadowRoot.querySelector('.widget-outer');
  const widgetFlowPathDefinition = selector.closest('[path]') ? "path" : "flow";
  const widgetFlowPathValue = selector.closest('[' + widgetFlowPathDefinition + ']').getAttribute(widgetFlowPathDefinition);
  const widget = selector.closest('['+ widgetFlowPathDefinition + '="' + widgetFlowPathValue + '"]');
  smoothScrollToElement(widget, findUserData.scrollToTopDuration ? findUserData.scrollToTopDuration : 500);
}

const actionExecutePrepareCallServices = (action, dispatch, formValues, setValues) => {
  dispatch(servicesLoadingAction());
  const {
    updateLoader,
    lastService,
    useSpinner,
    services,
  } = action.props;
  const serviceProps = {
    lastService,
    useSpinner,
  };

  const newServices = services.map((service) => {
    const newService = { ...service };
    const {
      serviceName,
      serviceParams,
      translateParams,
    } = newService;

    newService.serviceName = replaceData(serviceName, { ...formValues });
    if (translateParams) {
      newService.serviceParams = serviceParams.reduce(
        (serviceParameters, param) => {
          const newParam = replaceData(param.field, { ...formValues });
          const parameterObject = newParam.split('.')
            .reduce((obj, index) => obj[index], formValues);
          const parameters = Object.keys(parameterObject)
            .reduce((result, key) => {
              result.push({ field: key, value: parameterObject[key] });
              return result;
            }, []);
          return [...serviceParameters, ...parameters];
        }, []);
    }

    return newService;
  });
  callServices(
    newServices,
    updateLoader,
    serviceProps,
    {
      ...formValues,
      setValues,
    },
    dispatch);
  dispatch(servicesLoadedAction());
};

const actionExecuteNextStep = (action, dispatch, formValues, setValues) => {
  // Ensure that any modal is closed before going to next step.
  dispatch(appModalActivatorAction({ active: false }));
  dispatch(userDataFormSubmitAction({ ...formValues, setValues }));
  if (action.props && action.props.changeUserData) {
    const { props } = action;
    dispatch(userDataChangeAction({ ...props, formValues, setValues }));
  }
  dispatch(nextStepAction());
};

const actionExecuteChangeSvgText = (action, formValues) => {
  const properties = action.props;
  const { city } = formValues;
  const icon = icons.default[properties.icon];
  const prevCity = icon.indexOf(city);
  icons.default[properties.icon] =
      icon.replace(new RegExp(properties.toReplace, 'gi'), city);
  properties.toReplace = city;
  if (prevCity === -1) {
    document.getElementById('popupIllustration').classList.add('animate');
    setTimeout(() => {
      document.getElementById('popupIllustration')
        .classList
        .remove('animate');
    }, 1000);
  }
};

export const actionExecute = (action, dispatch, formValues, setValues) => {
  let isValid = true;
  if (action.requiredData) {
    isValid = stepHasRequiredData(action.requiredData, formValues, formValues);
  }
  if (isValid) {
    switch (action.actionType) {
      case 'nextStep': {
        actionExecuteNextStep(action, dispatch, formValues, setValues);
        break;
      }
      case 'prevStep': {
        // Ensure that any modal is closed before going to previous step.
        dispatch(prevStepAction());
        break;
      }
      case 'modal': {
        const modalData = {
          ...action.props,
          active: true,
        };
        dispatch(appModalActivatorAction(modalData));
        break;
      }
      case 'closeModal': {
        dispatch(appModalActivatorAction({ active: false }));
        break;
      }
      case 'callService': {
        const { serviceName, serviceParams, forProduct } = action.props;
        callService(
          serviceName,
          serviceParams,
          forProduct,
          {
            ...formValues,
            setValues,
          },
          dispatch
        );
        break;
      }
      case 'callServices': {
        dispatch(servicesLoadingAction());
        const {
          services,
          updateLoader,
          lastService,
          useSpinner,
          saveConsentService,
          generateLeadService,
        } = action.props;
        const serviceProps = {
          lastService,
          useSpinner,
          saveConsentService,
          generateLeadService,
        };
        callServices(
          services,
          updateLoader,
          serviceProps,
          {
            ...formValues,
            setValues,
          },
          dispatch);
        dispatch(servicesLoadedAction());
        break;
      }
      case 'prepareCallServices': {
        actionExecutePrepareCallServices(action, dispatch, formValues, setValues);
        break;
      }
      case 'elementToggler': {
        const { props: { id: elements } } = action;
        elements.forEach(element => {
          dispatch(elementTogglerAction(element));
        });
        break;
      }
      case 'changeUserData': {
        const { props } = action;
        dispatch(userDataChangeAction({ ...props, formValues, setValues }));
        break;
      }
      case 'scrollToTop': {
        actionExecuteScrollToTop(action);
        break;
      }
      case 'scrollToTopWidget': {
        scrollToTop(userData);
        break;
      }
      case 'changeSvgText': {
        actionExecuteChangeSvgText(action, formValues);
        break;
      }
      case 'refreshState': {
        localStorage.clear();
        location.reload(); // eslint-disable-line
        break;
      }
      case 'flowEvent': {
        const { props } = action;
        const data = {};
        if (props.data) {
          Object.keys(props.data).forEach(key => {
            data[key] = replaceData(props.data[key], formValues);
          });
        }
        createFlowEvent(props.type, data);
        break;
      }
      case 'saveState': {
        // Used as a placeholder for saving the form values to state.
        break;
      }
      case 'callTracking': {
        const { trackingStepNo } = action.props;
        if (trackingStepNo) {
          sendTrygTealium(trackingStepNo, formValues);
        }
        break;
      }
      case 'widgetEvent': {
        const policynumber = sessionStorage.getItem('policyNumber');
        const dataList = {};
          dataList.actions = action.props.triggeredOn;
          dataList.policyNumber = policynumber;
        const event = new CustomEvent("widgetEvents", { detail: dataList });
        window.dispatchEvent(event);
        break;
      }
      case 'finaCombinedServices':
      case 'combinedCusPurchase': {
        const { serviceName, serviceParams } = action.props;
        callService(serviceName, serviceParams, null, formValues, dispatch);
        break;
      }
      default:
        console.warn('❌ The action fired doesn\'t exist'); // eslint-disable-line
        break;
    }
  }
};

/**
 * Execute the action of the form items
 * @param   {object}          componentActions What event to be fired after the actions are done.
 * @param   {function|object} eventAction      Execute the action.
 * @param   {object}          event            The event param for the eventAction.
 * @param   {function}        dispatch         The dispatch function of the store.
 * @param   {object}          formValues       The current form values.
 * @param   {function}        setValues        The setValues from the form.
 * @param   {object}          extraData        Save extra data in the state.
 * @param   {boolean}         actionEnterEnabled If action enter is enabled or not
 * @returns {function}                         Fires the action and then the event.
 */
export const actionHandler = (
  componentActions,
  eventAction,
  event,
  dispatch,
  formValues = {},
  setValues = null,
  extraData = null,
  actionEnterEnabled = true
) => {
  // Save extra data in the state if some exists.
  if (extraData && Object.keys(extraData)) {
    dispatch(userDataFormSubmitAction({ ...extraData, setValues }));
  }
  if (componentActions && componentActions.length > 0) {
    const firstActions = [];
    const lastActions = componentActions.filter(action => {
      if (action.triggerOnEnter && !actionEnterEnabled) {
        return false;
      } else if (action.first) {
        firstActions.push(action);
        return false;
      }
      return true;
    });

    firstActions.forEach(action => {
      actionExecute(action, dispatch, formValues, setValues);
    });
    lastActions.forEach(action => {
      actionExecute(action, dispatch, formValues, setValues);
    });
  }

  if (!eventAction) {
    return {};
  }

  // If the eventAction is an array, then fire each of the actions
  if (Array.isArray(eventAction)) {
    return eventAction.forEach(action => {
      action(event);
    });
  }

  return eventAction(event);
};

/**
 * Make condition based on variant
 * @param   {array} conditions        Array of the conditions that should be met
 * @param   {object} userData         The data the user had already entered
 * @returns {boolean}
 */
export const getConditionVariant = (conditions, userData) => {
  let isTrue = false;
  conditions.forEach(condition => {
    const { conditions: conditionList } = condition;
    switch (condition.type) {
      case ('allIdsTrue'): {
        conditionList.forEach(conditionItem => {
          isTrue = userData[conditionItem] === true;
        });
        break;
      }
      case ('allIdsFalse'): {
        conditionList.forEach(conditionItem => {
          isTrue = userData[conditionItem] === false;
        });
        break;
      }
      case ('allIdsExists'): {
        const anyIdExistsInUserData = conditionItem => userData[conditionItem];
        if (conditionList.every(anyIdExistsInUserData)) {
          isTrue = true;
        }
        break;
      }
      case ('anyIdsTrue'): {
        conditionList.forEach(conditionItem => {
          if (isTrue === true) {
            return;
          }
          isTrue = userData[conditionItem] === true;
        });
        break;
      }
      case ('anyIdsFalse'): {
        conditionList.forEach(conditionItem => {
          if (isTrue === true) {
            return;
          }
          isTrue = userData[conditionItem] === false;
        });
        break;
      }
      case ('anyIdsExists'): {
        if (anyOfTheKeysExists(userData, conditionList)) {
          isTrue = true;
        }
        break;
      }
      default:
        isTrue = false;
    }
  });
  return isTrue;
};

/**
 * Custom function to set form values based on a template.
 * @param {string}   name               The name of the current field.
 * @param {string}   value              Value of the current field.
 * @param {array}    formValues         Array of formvalues where this should be saved.
 * @param {string}   formValuesTemplate A string templating how the value should look.
 * @param {object}   values             A string templating how the value should look.
 * @param {function} setFieldValue      The setFieldValue function of the form.
 */
export const setFormValues = (
  name,
  value,
  formValues,
  formValuesTemplate,
  values,
  setFieldValue
) => {
  // If we shouldn't save the value to a custom field we just return.
  if (!formValues) {
    return;
  }

  formValues.reduce((result, item) => {
    const newResult = result;
    let newValue = value;

    // If the template is empty we just add the value.
    if (formValuesTemplate) {
      newValue = formValuesTemplate;
      let find = `\\$\\[${name}\\]`;
      /* eslint-disable-line */
      let re = new RegExp(find, 'g');
      newValue = newValue.replace(re, value);
      // Loop current form values and add those that are in the template.
      Object.entries(values).forEach(([key, formValue]) => {
        if (key === name) {
          return;
        }
        find = `\\$\\[${key}\\]`;
        /* eslint-disable-line */
        re = new RegExp(find, 'g');
        newValue = newValue.replace(re, formValue);
      });
      // Remove unused tokens.
      const tokens = '\\$\\[[^\\]]*\\]';
      const regex = new RegExp(tokens, 'g');
      if (newValue.match(tokens)) {
        newValue = value.replace(regex, '');
      }
    }

    setFieldValue(item, newValue);
    return newResult;
  }, {});
};

/**
 * Gets a wait message for the step. It will use the last wait message to ensure we only show one.
 * @param  {array}  requiredData Data that should be set.
 * @param  {object} userData     The userData store.
 * @param  {object} formValues   The current form values.
 * @return {string}              The wait message.
 */
export const getStepWaitText = (requiredData, userData, formValues) => {
  const isStepValid = validateStep(requiredData, userData, formValues);
  return isStepValid.message;
};

/**
 * Show hide status on one item.
 * @param {boolean} status          The initial value for the status.
 * @param  {object}  item      Data that set the the show or hide of this component.
 * @param  {object} data    data to compare too.
 * @param  {object} userData      The userData store.
 * @return {boolean}              return true if it is to hide.
 */
const stepShowHideStatus = (status, item, data, userData) => {
  let newStatus = status;
  const key = replaceData(item.key, userData);
  if (item.type) {
    if (item.type === 'civilRegistrationNumber') {
      newStatus =
        status !== null ?
          status || validateCpr(data.civilRegistrationNumber) :
          validateCpr(data.civilRegistrationNumber);
    }
    if (item.type === 'minNumber') {
      newStatus =
        (status !== null || (data[item.key] > 0 && data[item.key] !== undefined)) ?
          status || data[item.key] >= item.value :
          status;
    }

    if (item.type === 'maxNumber') {
      newStatus =
        (status !== null || (data[item.key] > 0 && data[item.key] !== undefined)) ?
          status || data[item.key] < item.value :
          status;
    }

    if (item.type === 'minLength') {
      newStatus =
        (status !== null || (data[item.key] > 0 && data[item.key] !== undefined)) ?
          status || data[item.key].length >= item.value :
          status;
    }
    if (item.type === 'list' ) {
      let newData = '';
      if (data[item.key] && data[item.key] !== '') {
        newData = (data[item.key]).toUpperCase();
      }
      newStatus = newData !== ''? !item.value.includes(newData) : status;
    }
  } else if (item.notKey) {
    const notKey = replaceData(item.notKey, data);
    newStatus =
      status !== null ?
        status || Object.hasOwnProperty.call(data, notKey) :
        Object.hasOwnProperty.call(data, notKey);
  } else if (Object.hasOwnProperty.call(data, key)) {
    if (typeof item.notValue !== 'undefined') {
      newStatus =
        status !== null ?
          status || !(data[key] !== item.notValue) :
          !(data[key] !== item.notValue);
    } else if (item.includes) {
      newStatus =
        status !== null ?
          status || !(data[key].includes(item.includes)) :
          !(data[key].includes(item.includes));
    } else {
      newStatus =
        status !== null ?
          status || !(data[key] === item.value) :
          !(data[key] === item.value);
    }
  } else if (key && !Object.hasOwnProperty.call(data, key)) {
    newStatus = true;
  }
  if (newStatus && item.or) {
    newStatus = stepShowHideStatus(!newStatus, item.or, data, userData);
  }
  if (!newStatus && item.and) {
    newStatus = stepShowHideStatus(newStatus, item.and, data, userData);
  }
  return newStatus;
};

/**
 * Show hide component based on .
 * @param {boolean} hide          The initial value for the component.
 * @param  {object} showHide      Data that set the the show or hide of this component.
 * @param  {object} userData      The userData store.
 * @param  {object} formValues    The current form values.
 * @return {boolean}              return true if it is to hide.
 */
export const stepShowHide = (hide, showHide, userData, formValues) => {
  let status = null;
  if (showHide) {
    if (Object.hasOwnProperty.call(showHide, 'userData')) {
      status = stepShowHideStatus(status, showHide.userData, userData,
        userData);
      if (Object.hasOwnProperty.call(showHide.userData, 'multiple')) {
        const keyStatus = showHide.userData.multiple.filter((item) => {
          return !stepShowHideStatus(status, item, userData, userData);
        }).length;
        status = keyStatus <= 0;
      }
      if (Object.hasOwnProperty.call(showHide.userData, 'multipleAnd')) {
        const keyStatus = showHide.userData.multipleAnd.filter((item) => {
          return !stepShowHideStatus(status, item, userData, userData);
        }).length;
        status = keyStatus !== showHide.userData.multipleAnd.length;
      }
    }
    if (Object.hasOwnProperty.call(showHide, 'formValues')) {
      const hideValues = showHide.formValues;
      if(hideValues.type && hideValues.type ==="list") {
        const key = replaceData(hideValues.value, userData, false, false, false);
        hideValues['value'] = key;
      }
      status = stepShowHideStatus(status, hideValues, formValues, userData);
      if (Object.hasOwnProperty.call(showHide.formValues, 'multiple')) {
        const keyStatus = showHide.formValues.multiple.filter((item) => {
          return stepShowHideStatus(status, item, formValues, userData);
        }).length;
        status =
          status !== null ?
            status && keyStatus <= 0 :
            keyStatus <= 0;
      }
      if (Object.hasOwnProperty.call(showHide.formValues, 'multipleAnd')) {
        const keyStatus = showHide.formValues.multipleAnd.filter((item) => {
          return stepShowHideStatus(status, item, formValues, userData);
        }).length;
        status =
          status !== null ?
            status && keyStatus !== showHide.formValues.multipleAnd.length :
            keyStatus !== showHide.formValues.multipleAnd.length;
      }
    }
  }

  return status !== null ? status : hide;
};

/**
 * Toggle type component based on .
 * @param  {object} toggleType    Toggle type data.
 * @param  {object} userData      The userData store.
 * @param  {object} formValues    The current form values.
 * @return {object}               Object containing if the step is valid and a text if not.
 */
export const stepToggleType = (toggleType, userData, formValues) => {
  let status = false;
  if (toggleType) {
    toggleType.props.forEach((element) => {
      if (Object.hasOwnProperty.call(element, 'formValues') &&
        Object.hasOwnProperty.call(formValues, element.formValues.key)) {
        status = (formValues[element.formValues.key].toString() ===
          element.formValues.value);
      }
      if (Object.hasOwnProperty.call(element, 'userData') &&
        Object.hasOwnProperty.call(userData, element.userData.key)) {
        status = (userData[element.userData.key].toString() ===
          element.userData.value);
      }
    });
    return status ? toggleType.to : toggleType.from;
  }
  return null;
};